import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Download, Search, FileText, ExternalLink } from 'lucide-react';

// Configuration constants for discharge form URLs
const CONFIG = {
  MSA_NATIONAL_BASE_URL: process.env.REACT_APP_MSA_NATIONAL_BASE_URL || 'https://www.msanational.com.au/forms',
  // External lender URLs - could be moved to env variables if needed for different deployments
  EXTERNAL_URLS: {
    ANZ_SETTLE: 'https://www.anz.com/settle',
    WESTPAC_SECURITY: 'https://www.westpac.com.au/personal-banking/home-loans/manage-home-loan/changing-your-home-loan-security/',
    CBA_DISCHARGE: 'https://www.commbank.com.au/digital/home-loan-forms/mortgage-discharge',
    STGEORGE_SECURITY: 'https://www.stgeorge.com.au/securityrelease',
    ING_DISCHARGE: 'https://campaigns.ing.com.au/discharge-home-loan',
    NAB_DISCHARGE: 'https://www.nab.com.au/personal/home-loans/manage/mortgage-discharge-form-checklist'
  }
};

const DischargesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const dischargeData = [
    // A
    { name: 'Adelaide Bank', type: 'contact', note: 'Please contact Adelaide Bank for their Discharge Authority Form' },
    { name: 'Advantedge', type: 'download', url: `${CONFIG.MSA_NATIONAL_BASE_URL}/Discharge%20Authority%20(Advantedge)%200323.pdf` },
    { name: 'AFG Home Loans Edge (Advantedge)', type: 'download', url: `${CONFIG.MSA_NATIONAL_BASE_URL}/Discharge%20Authority%20(AFG)%200323.pdf` },
    { name: 'AFM', type: 'contact', note: 'Please contact Australian First Mortgage on 13 AFM (134 236)' },
    { name: 'AIMS', type: 'download', url: 'https://www.msanational.com.au/forms/AIMS.pdf' },
    { name: 'AMP', type: 'contact', note: 'Please contact 13 30 30 for requests to discharge' },
    { name: 'Ample Capital Australia', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Ample%20Capital%20Australia.pdf' },
    { name: 'AMS - Mortgage Services Pty Ltd', type: 'download', url: 'https://www.msanational.com.au/forms/204028-LHL-Full%20Discharge%20Authority%20Form-1102-V3_Pepper.pdf' },
    { name: 'Anchorage Home Loans', type: 'download', url: 'https://www.msanational.com.au/forms/204028-LHL-Full%20Discharge%20Authority%20Form-1102-V3_Pepper.pdf' },
    { name: 'ANZ', type: 'external', url: CONFIG.EXTERNAL_URLS.ANZ_SETTLE, note: 'Visit ANZ Settle portal' },
    { name: 'API Home Loans', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20API%20Home%20Loans.pdf' },
    { name: 'Arab Bank', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Arab%20Bank.pdf' },
    { name: 'Astute Simplicity Home Loan (Advantedge)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(Astute)%200323.pdf' },
    { name: 'Aussie Home Loans', type: 'contact', note: 'Please contact Aussie for their Discharge Authority Form' },
    { name: 'Austral Credit Union', type: 'download', url: 'https://www.msanational.com.au/forms/Austral%20Credit%20Union.pdf' },
    { name: 'Australian Financial (AF)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Australian%20Financial.pdf' },
    { name: 'Australian Lending Services', type: 'download', url: 'https://www.msanational.com.au/forms/Australian%20Lending%20Services.pdf' },
    { name: 'Australian Military Bank / Australian Defence Credit Union', type: 'external', url: 'https://www.australianmilitarybank.com.au/sites/default/files/pdf/Authority_Release_Existing_Mortgage.pdf' },
    { name: 'Australian Mutual Bank Limited (Formerly Select Credit Union)', type: 'download', url: 'https://www.msanational.com.au/forms/AMBL_DischargeAuthority_8Nov21.pdf' },
    { name: 'Australian Unity Bank (Formerly Big Sky)', type: 'download', url: 'https://www.msanational.com.au/forms/AU0397_Australian%20Unity%20Discharge%20Authority.pdf' },
    { name: 'Australian Wholesale Lending Mortgages', type: 'download', url: 'https://www.msanational.com.au/forms/Australian-Wholesale-Lending-Mortgages.pdf' },
    { name: 'Auswide Bank (Formerly Wide Bay Capricorn)', type: 'contact', note: 'Please contact Auswide Bank on 1300 138 831' },

    // B
    { name: 'Bank Australia (Formerly Intech Credit Union)', type: 'contact', note: 'Please contact Bank Australia on 132 888' },
    { name: 'Bank of Melbourne', type: 'external', url: 'https://psr.bankofmelbourne.com.au/' },
    { name: 'Bank of Queensland', type: 'contact', note: 'Please contact your local branch for discharge form' },
    { name: 'Bank of South Australia', type: 'external', url: 'https://www.banksa.com.au/personal/home-loans/manage/mortgage-security' },
    { name: 'Bank of Western Australia / BankWest', type: 'external', url: 'https://www.bankwest.com.au/content/dam/bankwest/documents/legal-library/PDS_BWE-1178.pdf' },
    { name: 'BDCU Alliance Bank', type: 'download', url: 'https://www.msanational.com.au/forms/AuthoritytoDischargeSecuritiesandInformationRelease_1503v2.pdf' },
    { name: 'Bendigo Bank', type: 'contact', note: 'Please contact Bendigo Bank for discharge form' },
    { name: 'Berrima District Credit Union Alliance', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Berrima%20District%20Credit%20Union.pdf' },
    { name: 'Better Choice Home Loans (Formerly Iden Group)', type: 'contact', note: 'Please contact 1300 334 336 to initiate discharge' },
    { name: 'Better Mortgage Management', type: 'contact', note: 'Call 1300 662 661 for correct form (multiple funders)' },
    { name: 'Beyond Bank', type: 'external', url: 'https://forms.beyondbank.com.au/beyondbank/servlet/SmartForm.html?formCode=sf050' },
    { name: 'Bluegum Home Loans', type: 'download', url: 'https://www.msanational.com.au/forms/Bluegum-Discharge-Authority.pdf' },
    { name: 'Bluestone', type: 'download', url: 'https://www.msanational.com.au/forms/Bluestone.pdf' },
    { name: 'BMC Mortgage Group', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge_Authority_BMC_Advantedge.pdf' },

    // C
    { name: 'ChoiceLend', type: 'external', url: 'https://choicelend.com.au/forms' },
    { name: 'ChoiceLend (Advantedge)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(Choicelend)%200323.pdf' },
    { name: 'Citigroup Pty Ltd', type: 'download', url: 'https://www.msanational.com.au/forms/mortgage_request_discharge_Citibank.pdf' },
    { name: 'Coastline Credit Union', type: 'contact', note: 'Call 1300 36 1066 or email: loans@coastline.com.au' },
    { name: 'Collins Home Loans (Formerly Collins Securities)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Collins%20Securities%200609.pdf' },
    { name: 'Commonwealth Bank of Australia', type: 'external', url: CONFIG.EXTERNAL_URLS.CBA_DISCHARGE },
    { name: 'Community First Credit Union', type: 'contact', note: 'Please contact on 1300 13 22 77' },
    { name: 'Community First Credit Union (Formerly Northern Beaches)', type: 'contact', note: 'Please contact 1300 131 964' },
    { name: 'Connective Elevate', type: 'download', url: 'https://www.msanational.com.au/forms/Elevate.pdf' },
    { name: 'Connective Home Loans Essentials (Advantedge)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(Connective)%200323.pdf' },
    { name: 'Credit Union Home Loans', type: 'download', url: 'https://www.msanational.com.au/forms/Credit%20Union%20Home%20Loans.pdf' },

    // D
    { name: 'Defence Bank Limited', type: 'contact', note: 'Call 1800 033 139 or email info@defencebank.com.au' },
    { name: 'Destiny Mortgage Solutions', type: 'contact', note: 'Please contact 02 4351 0380' },
    { name: 'Direct Mortgage Solutions', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Direct%20Mortgage%20Solutions(2).pdf' },
    { name: 'Director of Housing Victoria', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority_Director%20of%20Housing%20Victoria.pdf' },

    // F
    { name: 'FastLend', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(Fastlend)%200323.pdf' },
    { name: 'First Australian Building Society', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20First%20Australian%20Building%20Society.(2)pdf.pdf' },
    { name: 'Folio Mortgage and Finance (Formerly First Folio)', type: 'contact', note: 'Contact on 1300 304 572 or email variations@foilo.com.au' },
    { name: 'Fox Symes Home Loans', type: 'contact', note: 'Please contact on 1300 889 479' },

    // G
    { name: 'G&C Mutual Bank | Quay Mutual Bank', type: 'download', url: 'https://www.msanational.com.au/forms/G&C-Mutual-Bank.pdf' },
    { name: 'Gateway Credit Union', type: 'download', url: 'https://www.msanational.com.au/forms/Authority%20to%20Release%20August%202018_Gateway.pdf' },
    { name: 'GE Money', type: 'download', url: 'https://www.msanational.com.au/forms/GE-Money-Discharge-Authority.pdf' },
    { name: 'GE Personal Finance Pty Ltd', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20GE%20Money%20-%20GE%20Personal%20Finance.pdf', note: 'For other GE loans contact Pepper Home Loans' },
    { name: 'Go Edge (Advantedge)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(GoEdge)%200323.pdf' },
    { name: 'Great Southern Loans', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Great%20Southern%20Loans%2002%2009%20v2.pdf' },

    // H
    { name: 'Heritage Bank', type: 'contact', note: 'Please contact Heritage Bank for discharge form' },
    { name: 'Home Loan Centre Australia', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Home%20Loan%20Centre%20Australia.pdf' },
    { name: 'Home Loans Ltd', type: 'contact', note: 'Please contact Home Loans Ltd for discharge form' },
    { name: 'Home Path', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Home%20Path.pdf' },
    { name: 'Homeside Lending', type: 'download', url: 'https://www.msanational.com.au/forms/Homeside_Discharge_Form_Homeside%20Lending.pdf' },
    { name: 'Homestart Finance', type: 'download', url: 'https://www.msanational.com.au/forms/Notice_for_Discharge_of_Mortgage_Homestart%20Finance.pdf' },
    { name: 'HSBC', type: 'contact', note: 'Contact Relationship Manager, local branch or phone 1300 301 168' },
    { name: 'Hunter United - A division of IMB', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority_Hunter%20United.pdf' },

    // I
    { name: 'Illawarra Credit Union (Formerly Catalyst)', type: 'download', url: 'https://www.msanational.com.au/forms/ICU%20Discharge%20Form_Illawarra%20Credit%20Union.pdf' },
    { name: 'ING', type: 'external', url: CONFIG.EXTERNAL_URLS.ING_DISCHARGE },
    { name: 'Interstar', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Interstar.pdf' },

    // L
    { name: 'La Trobe Financial', type: 'contact', note: 'Please contact on 13 80 10' },
    { name: 'Liberty Financial', type: 'contact', note: 'Call 13 11 33' },
    { name: 'LJ Hooker Home Loans Connect (Advantedge)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(LJHooker)%200323.pdf' },
    { name: 'Loanave', type: 'contact', note: 'Please contact on 1300 562 628' },
    { name: 'Loanworks (Formerly Sintex)', type: 'download', url: 'https://www.msanational.com.au/forms/Loanworks%20Loan%20Discharge%20Authority%20Form.pdf' },

    // M
    { name: '101 Mortgage Finance Solutions', type: 'contact', note: 'Please contact on 1300 101 101' },
    { name: 'Macquarie Bank - Refinances', type: 'contact', note: 'Contact Macquarie for refinance discharges' },
    { name: 'Macquarie Bank - Sales or Partial Discharges', type: 'external', url: 'https://www.macquarie.com.au/assets/bfs/documents/personal-direct/home-loans/home-loan_discharge-authority-form.pdf' },
    { name: 'ME Bank', type: 'contact', note: 'Please contact 1300 364 398' },
    { name: 'MKM Capital', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge-Form-MKM-Loan.pdf' },
    { name: 'Mortgage Choice Home Loans Amplify', type: 'download', url: 'https://www.msanational.com.au/forms/MortgageChoice.pdf' },
    { name: 'Mortgage Choice Home Loans SmartSelect (Advantedge)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(MCHL)%200323.pdf' },
    { name: 'Mortgage Ezy', type: 'contact', note: 'Please call 1300 835 399' },
    { name: 'Mortgage House', type: 'download', url: 'https://www.msanational.com.au/forms/Mortgage-House.pdf' },
    { name: 'Mortgage Port', type: 'contact', note: 'Please contact on 1300 720 110' },
    { name: 'Mortgage Wisdom (Formerly Mortgage Plus)', type: 'contact', note: 'Please contact on 02 4225 7481' },

    // N
    { name: 'Napier', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Napiers.pdf' },
    { name: 'National Australia Bank', type: 'external', url: CONFIG.EXTERNAL_URLS.NAB_DISCHARGE },
    { name: 'National Mortgage Market Corporation (NMMC)', type: 'contact', note: 'Please contact NMMC directly' },
    { name: 'NCF Financial Services', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20NCF.pdf' },
    { name: 'Newcastle Permanent Building Society', type: 'contact', note: 'Please contact 13 19 87' },
    { name: 'NSW Teachers Credit Union', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20NSW%20Teachers%20Credit%20Union(2).pdf' },

    // O
    { name: 'Origin Mortgage Management Services', type: 'contact', note: 'Contact 1300 767 023 or email: service@originmms.com.au' },
    { name: 'Origin Mortgage Management (Formerly Columbus Capital)', type: 'contact', note: 'Contact 1300 767 023 or email: service@originmms.com.au' },

    // P
    { name: "People's Choice Credit Union", type: 'contact', note: 'Contact team on 8124 2137' },
    { name: 'Pepper', type: 'download', url: 'https://www.msanational.com.au/forms/204028-LHL-Full%20Discharge%20Authority%20Form-0510-V4.pdf' },
    { name: 'Perpetual Trustees Australia', type: 'contact', note: 'Please contact 02 9229 9888' },
    { name: 'Perpetual Trustees Victoria Limited (Advantedge)', type: 'contact', note: 'Please contact 02 9229 9888' },
    { name: 'Pioneer Mortgage Services', type: 'download', url: 'https://www.msanational.com.au/forms/Pioneer-Discharge-Form-Final-010514.pdf' },
    { name: 'PlanLend (Advantedge)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(Planlend)%200323.pdf' },
    { name: 'Power Loan (Formerly Power Home Loans)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Form%20(Advantedge)%200619.pdf' },

    // Q
    { name: 'Qudos Bank (Formerly Qantas Credit Union)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge-Authority-Form-QudosBank.pdf' },
    { name: 'Queensland Country Bank (Formerly Queensland Country Credit Union)', type: 'contact', note: 'Contact your nearest branch' },
    { name: 'Queenslanders Credit Union', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Queenslanders%20CU.pdf' },

    // R
    { name: 'Rabobank', type: 'download', url: 'https://www.msanational.com.au/forms/Form-Account-Closure-Discharge-Authority_AU_Rabobank.pdf' },
    { name: 'RAMS', type: 'contact', note: 'Please call 13 72 67' },
    { name: 'Regional Australia Bank (Formerly Community Mutual)', type: 'contact', note: 'Please contact on 13 20 67' },
    { name: 'Regional Australia Bank (Formerly Holiday Coast Credit Union)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority_Regional%20Bank%20Australia.pdf' },
    { name: 'Reliance Bank', type: 'contact', note: 'Please call Reliance Bank to initiate discharge' },
    { name: 'Resi - Puma/ING', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20RESI%20to%20June%202021.pdf' },
    { name: 'Resi (Advantedge)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(Resi)%200323.pdf' },
    { name: 'Resi Mortgage Corporation', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20RESI%20to%20June%202021.pdf' },
    { name: 'Resimac', type: 'external', url: 'https://broker.resimac.com.au/-/media/Project/Resimac/Broker/Files/RES_Full-Discharge-Authority-Form.pdf' },
    { name: 'RHG Mortgage Corporation Limited', type: 'download', url: 'https://www.msanational.com.au/forms/RHG-Full-Discharge-Authority-Form.pdf' },

    // S
    { name: 'Sandhurst Trustees', type: 'download', url: 'https://www.msanational.com.au/forms/Sandhurst%20Trustees%20Discharge%20Authority%20Form.pdf' },
    { name: 'Southern Cross Credit Union', type: 'contact', note: 'Please contact directly to initiate discharge' },
    { name: 'St George Bank', type: 'external', url: CONFIG.EXTERNAL_URLS.STGEORGE_SECURITY },
    { name: 'St George Bank - Partial Discharge - Substitute Security', type: 'external', url: CONFIG.EXTERNAL_URLS.STGEORGE_SECURITY },
    { name: 'St George WA', type: 'external', url: CONFIG.EXTERNAL_URLS.STGEORGE_SECURITY },
    { name: 'Statewide Home Loans', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Statewide.pdf' },
    { name: 'Suncorp', type: 'contact', note: 'Please contact on 13 11 55' },
    { name: 'Sydney Home Loans', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Sydney%20Home%20Loans.pdf' },

    // U
    { name: 'UBank', type: 'contact', note: 'Please contact UBank for discharge form' },
    { name: 'Unity Bank (Formerly Maritime Mining Power Credit Union)', type: 'contact', note: 'Please contact 1300 362 000' },

    // V
    { name: 'Virgin Money', type: 'download', url: 'https://www.msanational.com.au/forms/Virgin%20Money%20DA.pdf' },

    // W
    { name: 'Westeast Home Loans', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20Westeast%20Home%20Loans.pdf' },
    { name: 'Westpac Banking Corporation', type: 'external', url: CONFIG.EXTERNAL_URLS.WESTPAC_SECURITY },

    // Y
    { name: 'Yellow Brick Road', type: 'contact', note: 'Please contact on 1800 927 927' },
    { name: 'YellowBrickRoad Home Loans (Advantedge)', type: 'download', url: 'https://www.msanational.com.au/forms/Discharge%20Authority%20(YBR)%200323.pdf' },
    { name: 'Yes Home Loans', type: 'download', url: 'https://www.msanational.com.au/forms/Yes-Home-Loans.pdf' }
  ];

  const filteredData = dischargeData.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getActionButton = (item) => {
    switch (item.type) {
      case 'download':
        return (
          <Button 
            onClick={() => window.open(item.url, '_blank')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
        );
      case 'external':
        return (
          <Button 
            onClick={() => window.open(item.url, '_blank')}
            variant="outline"
            className="border-green-600 text-green-600 hover:bg-green-50"
            size="sm"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Visit Website
          </Button>
        );
      case 'contact':
        return (
          <Button 
            disabled
            variant="outline"
            className="border-orange-400 text-orange-600 cursor-not-allowed"
            size="sm"
          >
            <FileText className="mr-2 h-4 w-4" />
            Contact Required
          </Button>
        );
      default:
        return null;
    }
  };

  const downloadCount = dischargeData.filter(item => item.type === 'download').length;
  const externalCount = dischargeData.filter(item => item.type === 'external').length;
  const contactCount = dischargeData.filter(item => item.type === 'contact').length;

  return (
    <div className="min-h-screen flex flex-col bg-page">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            Discharge Forms Directory
          </h1>
          <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
            Access discharge authority forms from major Australian lenders
          </p>
          <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
            Search through {dischargeData.length}+ lenders and financial institutions
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">{downloadCount}</div>
              <div className="text-gray-400">Direct Downloads</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">{externalCount}</div>
              <div className="text-gray-400">External Links</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-400">{contactCount}</div>
              <div className="text-gray-400">Contact Required</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by lender name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 py-3 text-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <p className="text-center text-gray-600 mt-3">
            {filteredData.length} of {dischargeData.length} lenders shown
          </p>
        </div>
      </div>

      {/* Forms Section */}
      <div className="py-12 bg-white flex-1">
        <div className="max-w-6xl mx-auto px-4">
          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="text-amber-600 mt-1">⚠️</div>
              <div>
                <h3 className="font-semibold text-amber-800 mb-1">Important Disclaimer</h3>
                <p className="text-amber-700 text-sm">
                  These discharge authorities are provided for assistance. Please ensure forms are in the current format 
                  required by the relevant mortgagee. <strong>No responsibility is taken by MSA National as to the use 
                  of or the currency of the forms.</strong> Always verify with the lender for the most current form.
                </p>
              </div>
            </div>
          </div>

          {/* Forms Grid */}
          <div className="grid gap-4 md:gap-6">
            {filteredData.map((item, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {item.name}
                      </h3>
                      {item.note && (
                        <p className="text-sm text-gray-600 mb-3">
                          {item.note}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`px-2 py-1 rounded-full text-white ${
                          item.type === 'download' ? 'bg-blue-600' :
                          item.type === 'external' ? 'bg-green-600' :
                          'bg-orange-500'
                        }`}>
                          {item.type === 'download' ? 'PDF Download' :
                           item.type === 'external' ? 'External Link' :
                           'Contact Required'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      {getActionButton(item)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredData.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No lenders found</h3>
              <p className="text-gray-600">
                Try adjusting your search terms or browse all available forms.
              </p>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DischargesPage;